import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLeaderboardPoll } from '../hooks/useLeaderboardPoll';
import { useEvent } from '../hooks/useEvent';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { sidegamesApi } from '../api/sidegames';
import { ApiError } from '../api/client';
import type { SideGameClaim, SideGameKind } from '../api/types';

type PromoteHandler = (claim: SideGameClaim) => void | Promise<void>;

type DeletePrompt = {
  kind: SideGameKind;
  claim: SideGameClaim;
};

export function EventLeaderboard() {
  const { id } = useParams<{ id: string }>();
  const { data: eventDetail } = useEvent(id);
  const { actor } = useAuth();
  const lb = useLeaderboardPoll(id);
  const [deletePrompt, setDeletePrompt] = useState<DeletePrompt | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isAdmin =
    !!eventDetail &&
    actor?.kind === 'user' &&
    eventDetail.event.admin_user_id === actor.user.id;

  const onDelete = async () => {
    if (!deletePrompt || !id) return;
    setBusy(true);
    setErr(null);
    try {
      const { kind, claim } = deletePrompt;
      if (kind === 'kp') await sidegamesApi.deleteKp(id, claim.holeNumber, claim.id);
      else if (kind === 'ld') await sidegamesApi.deleteLd(id, claim.holeNumber, claim.id);
      else await sidegamesApi.deleteHio(id, claim.holeNumber, claim.id);
      await lb.refetch();
      setDeletePrompt(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not delete claim');
    } finally {
      setBusy(false);
    }
  };

  const promote = async (kind: 'kp' | 'ld', claim: SideGameClaim) => {
    if (!id) return;
    setErr(null);
    try {
      if (kind === 'kp') await sidegamesApi.promoteKp(id, claim.holeNumber, claim.id);
      else await sidegamesApi.promoteLd(id, claim.holeNumber, claim.id);
      await lb.refetch();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not promote claim');
    }
  };

  return (
    <div className="container stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>Leaderboard</h1>
        <Button variant="secondary" onClick={() => lb.refetch()} disabled={lb.isFetching}>
          {lb.isFetching ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      {eventDetail && (
        <div className="muted" style={{ fontSize: 13 }}>
          {eventDetail.event.name} · {eventDetail.event.format === 'best_ball_4some' ? 'Best Ball' : 'Stroke Play'}
        </div>
      )}

      {err && <div className="error">{err}</div>}
      {lb.error && (
        <div className="error">
          {lb.error instanceof Error ? lb.error.message : 'Failed to load leaderboard'}
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="scorecard">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>#</th>
              <th className="row-label" style={{ textAlign: 'left' }}>Player / Team</th>
              <th>Total</th>
              {eventDetail?.event.handicap_enabled && <th>Net</th>}
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {lb.data?.rows.map((r, i) => (
              <tr key={r.subjectId}>
                <td>{i + 1}</td>
                <td className="row-label">
                  <Link to={`/e/${id}/scorecard/${r.subjectId}`}>{r.name}</Link>
                </td>
                <td><strong>{r.holesPlayed === 0 ? '—' : r.total}</strong></td>
                {eventDetail?.event.handicap_enabled && <td>{r.net ?? '—'}</td>}
                <td>{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {lb.data && (
        <SideGameSection
          title="Closest to Pin"
          kind="kp"
          claims={lb.data.kp}
          isAdmin={isAdmin}
          onDelete={(c) => setDeletePrompt({ kind: 'kp', claim: c })}
          onPromote={(c) => promote('kp', c)}
        />
      )}

      {lb.data && (
        <SideGameSection
          title="Longest Drive"
          kind="ld"
          claims={lb.data.ld}
          isAdmin={isAdmin}
          onDelete={(c) => setDeletePrompt({ kind: 'ld', claim: c })}
          onPromote={(c) => promote('ld', c)}
        />
      )}

      {lb.data && (
        <SideGameSection
          title="Hole in One"
          kind="hio"
          claims={lb.data.hio}
          isAdmin={isAdmin}
          onDelete={(c) => setDeletePrompt({ kind: 'hio', claim: c })}
        />
      )}

      <Link to={`/e/${id}`}><Button variant="secondary" block>Back to lobby</Button></Link>

      <Modal
        open={!!deletePrompt}
        title="Delete this claim?"
        body={
          deletePrompt && (
            <p style={{ margin: 0 }}>
              Remove <strong>{deletePrompt.claim.name}</strong>'s claim on hole {deletePrompt.claim.holeNumber}?
            </p>
          )
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => setDeletePrompt(null)} disabled={busy}>Cancel</Button>
            <Button variant="danger" onClick={onDelete} disabled={busy}>Delete</Button>
          </>
        }
        onClose={() => setDeletePrompt(null)}
      />
    </div>
  );
}

function SideGameSection({
  title,
  kind,
  claims,
  isAdmin,
  onDelete,
  onPromote,
}: {
  title: string;
  kind: SideGameKind;
  claims: SideGameClaim[];
  isAdmin: boolean;
  onDelete: (c: SideGameClaim) => void;
  onPromote?: PromoteHandler;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const canReorder = isAdmin && kind !== 'hio' && !!onPromote;

  const grouped = new Map<number, SideGameClaim[]>();
  for (const c of claims) {
    const list = grouped.get(c.holeNumber) ?? [];
    list.push(c);
    grouped.set(c.holeNumber, list);
  }
  const holeNumbers = [...grouped.keys()].sort((a, b) => a - b);

  return (
    <div className="card stack">
      <h3 style={{ margin: 0 }}>{title}</h3>
      {canReorder && claims.length > 1 && (
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          Drag a claim within a hole to make it the current winner. Or use the ↑ button.
        </p>
      )}
      {claims.length === 0 && (
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          No claims yet.
        </p>
      )}
      {holeNumbers.map((hole) => {
        const rows = grouped.get(hole)!;
        return (
          <div key={hole} className="stack" style={{ gap: 0 }}>
            <div className="muted" style={{ fontSize: 12, marginBottom: 2 }}>Hole {hole}</div>
            {rows.map((r) => (
              <div
                key={r.id}
                className={`sg-row ${r.isCurrent && kind !== 'hio' ? 'sg-row-current' : ''}`}
                draggable={canReorder}
                onDragStart={(e) => {
                  if (!canReorder) return;
                  setDragId(r.id);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', r.id);
                }}
                onDragEnd={() => setDragId(null)}
                onDragOver={(e) => {
                  if (!canReorder) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(e) => {
                  if (!canReorder) return;
                  e.preventDefault();
                  const draggedId = e.dataTransfer.getData('text/plain') || dragId;
                  setDragId(null);
                  if (!draggedId) return;
                  const dropped = rows.find((c) => c.id === draggedId);
                  if (!dropped || dropped.isCurrent) return;
                  void onPromote?.(dropped);
                }}
                style={{ cursor: canReorder ? 'grab' : undefined, opacity: dragId === r.id ? 0.5 : 1 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {canReorder && <span style={{ color: 'var(--text-muted)', cursor: 'grab' }} aria-hidden>⠿</span>}
                  <strong>{r.name}</strong>
                  {r.isCurrent && kind !== 'hio' && (
                    <span className="badge" style={{ marginLeft: 4 }}>current</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {canReorder && !r.isCurrent && (
                    <button
                      type="button"
                      onClick={() => onPromote?.(r)}
                      aria-label="Make current"
                      title="Make this the current winner"
                      style={{
                        background: 'none',
                        border: '1px solid var(--color-green-700)',
                        color: 'var(--color-green-700)',
                        cursor: 'pointer',
                        borderRadius: 4,
                        padding: '0 6px',
                        fontSize: 14,
                        lineHeight: 1.2,
                      }}
                    >
                      ↑
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => onDelete(r)}
                      aria-label="Delete claim"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--color-danger)',
                        cursor: 'pointer',
                        fontSize: 16,
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
